import type { ReactNode } from "react";

export function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

export function Text({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  type?: string;
}) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <input
        className="input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  rows = 2,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  rows?: number;
}) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <textarea className="input" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
  hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** Lista de textos curtos (bullets do hero). Item vazio é descartado ao salvar. */
export function StringList({
  label,
  values,
  onChange,
  hint,
  placeholder,
  max,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  hint?: string;
  placeholder?: string;
  max?: number;
}) {
  const set = (i: number, v: string) => onChange(values.map((x, idx) => (idx === i ? v : x)));
  const remove = (i: number) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <Field label={label} hint={hint}>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className="input"
              value={v}
              placeholder={placeholder}
              onChange={(e) => set(i, e.target.value)}
            />
            <button
              type="button"
              className="btn-ghost !px-2 !py-1 text-xs"
              aria-label={`Remover item ${i + 1}`}
              onClick={() => remove(i)}
            >
              ✕
            </button>
          </div>
        ))}
        {(max === undefined || values.length < max) && (
          <button type="button" className="btn-ghost text-xs" onClick={() => onChange([...values, ""])}>
            + Adicionar
          </button>
        )}
      </div>
    </Field>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card space-y-4">
      <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">{title}</h2>
      {children}
    </section>
  );
}
